<template>
    <a-modal
        :title="isUpdate ? '修改帮助中心' : '新增帮助中心'"
        :width="900"
        :visible="visible"
        :confirmLoading="loading"
        :maskClosable="false"
        @ok="handleOk"
        @cancel="handleCancel"
    >
        <a-spin :spinning="loading">
            <a-form-model ref="adminForm" :model="form" :label-col="{span:5}" :wrapper-col="{span:15}" autoComplete="off">
                <a-form-model-item label="分类名称" prop="ident"
                    :rules="[
                        { required: true, message: '请选择分类', trigger: 'change' }
                    ]"
                >
                    <a-select
                        v-model="form.ident"
                        placeholder="请选择分类"
                        style="width: 100%"
                        @change="onCategoryChange"
                    >
                        <a-select-option
                            v-for="item in categoryOptions"
                            :key="item.ident"
                            :value="item.ident"
                        >{{ item.name }}</a-select-option>
                    </a-select>
                </a-form-model-item>
                <a-form-model-item label="标题" prop="title"
                    :rules="[
                {required: true, message: '必填，不能为空', trigger: 'blur'},
                {validator: this.isExistTitle, trigger: 'change'}
            ]"
                >
                    <a-input v-model="form.title" :disabled="isUpdate" placeholder="请输入标题" />
                </a-form-model-item>

                <a-form-model-item label="排序" prop="seq"
                    :rules="[
                        { required: true, message: '请输入排序值', trigger: 'change' }
                    ]"
                >
                    <a-input-number
                        v-model="form.seq"
                        :min="0"
                        :precision="0"
                        placeholder="排序值，数值越大越靠前"
                        style="width: 100%"
                    />
                </a-form-model-item>

                <a-form-model-item label="内容" prop="content"
                    :rules="[
                        {required: true, message: '必填，不能为空', trigger: 'change'}
                    ]"
                >
                    <div class="quill-editor-wrapper">
                        <quill-editor 
                            ref="quillEditor" 
                            v-model="form.content" 
                            :options="editorOption"
                            @change="onEditorChange"
                        />
                    </div>
                </a-form-model-item>
            </a-form-model>
        </a-spin>
    </a-modal>
</template>

<script>
import {
    quillEditor
} from 'vue-quill-editor';
import 'quill/dist/quill.core.css';
import 'quill/dist/quill.snow.css';
import 'quill/dist/quill.bubble.css';
import '@/assets/quill/quill.zh.css';
import quillOption from "@/assets/quill/quill.config";
import { helpPageTitleIsExist } from "@/api/content/helpPage.js";

/** ident 为库表 key，name 为展示名称 */
const HELP_PAGE_CATEGORY_OPTIONS = [
    { ident: 'normal', name: '常见问题' },
    { ident: 'account', name: '账号问题' },
    { ident: 'certifie', name: '认证问题' },
	{ ident: 'cashout', name: '提现问题' },
	{ ident: 'gold', name: '金币问题' },
    { ident: 'socre', name: '积分问题' },
    { ident: 'unfraud', name: '反诈防骗' },
];

const DEFAULT_SEQ = 50;

export default {
    components: {
        quillEditor
    },
    props: {
        visible: {
            type: Boolean,
            required: true
        },
        loading: {
            type: Boolean,
            default: () => false
        }
    },
    data() {
        return {
            form: {
                id: 0,
                ident: undefined,
                name: '',
                title: '',
                seq: DEFAULT_SEQ,
                content: ''
            },
            categoryOptions: HELP_PAGE_CATEGORY_OPTIONS,
            editorOption: quillOption,
            originalTitle: '', // 保存原始标题，用于修改时比较
            titleCheckTimer: null, // 防抖定时器
        }
    },
    computed: {
        isUpdate: function() {
            return this.form.id > 0 ? true : false;
        },
    },
    methods: {
        initForm() {
            this.form = {
                id: 0,
                ident: undefined,
                name: '',
                title: '',
                seq: DEFAULT_SEQ,
                content: ''
            }
            this.originalTitle = '';
            // 清除防抖定时器
            if (this.titleCheckTimer) {
                clearTimeout(this.titleCheckTimer);
                this.titleCheckTimer = null;
            }
            // 重置表单验证
            this.$nextTick(() => {
                if (this.$refs.adminForm) {
                    this.$refs.adminForm.clearValidate();
                }
            });
        },
        syncEditorContent() {
            // 同步编辑器内容
            if (this.$refs.quillEditor && this.$refs.quillEditor.quill) {
                const quill = this.$refs.quillEditor.quill;
                const currentContent = quill.root.innerHTML;
                const formContent = this.form.content || '';
                // 只有当内容不同时才更新，避免循环更新
                if (currentContent !== formContent && formContent !== '<p><br></p>') {
                    quill.clipboard.dangerouslyPasteHTML(formContent);
                }
            }
        },
        onEditorChange() {
            // vue-quill-editor 的 change 事件
            // 通过 v-model 已经自动同步了内容，这里只需要触发验证
            this.$nextTick(() => {
                if (this.$refs.adminForm) {
                    this.$refs.adminForm.validateField('content');
                }
            });
        },
        handleOk() {
            this.$emit('ok');
        },
        handleCancel() {
            this.$emit('cancel');
        },
        onCategoryChange(ident) {
            const item = this.categoryOptions.find(o => o.ident === ident);
            this.form.name = item ? item.name : '';
        },
        resolveCategoryIdent(record) {
            if (record.ident) {
                return record.ident;
            }
            const found = this.categoryOptions.find(o => o.name === record.name);
            return found ? found.ident : undefined;
        },
        isExistTitle(rule, value, callback) {
            // 如果标题为空，不进行验证
            if (!value || value.trim() === '') {
                callback();
                return;
            }
            // 如果是修改模式且标题没有变化，不进行验证
            // 需要保存原始标题值用于比较
            const originalTitle = this.originalTitle || '';
            if (this.isUpdate && value === originalTitle) {
                callback();
                return;
            }
            // 使用防抖，避免频繁请求
            if (this.titleCheckTimer) {
                clearTimeout(this.titleCheckTimer);
            }
            this.titleCheckTimer = setTimeout(() => {
                // 检查标题是否存在
                helpPageTitleIsExist(value).then(res => {
                    if (res && res.data) {
                        callback('标题已存在');
                    } else {
                        callback();
                    }
                }).catch(error => {
                    console.error('检查标题是否存在失败:', error);
                    callback();
                });
            }, 100); // 500ms 防抖延迟
        }
    },
    watch: {
        visible(newVal) {
            if (!newVal) {
                // 弹窗关闭时重置表单
                this.initForm();
            } else {
                // 弹窗打开时，保存原始标题值
                this.$nextTick(() => {
                    this.originalTitle = this.form.title || '';
                    this.syncEditorContent();
                });
            }
        },
        // 监听 form.content 变化，确保编辑器内容同步（用于修改时）
        'form.content'(newVal) {
            if (this.visible && newVal) {
                this.$nextTick(() => {
                    this.syncEditorContent();
                });
            }
        }
    }
}
</script>

<style scoped>
.quill-editor-wrapper {
    line-height: normal;
}

.quill-editor-wrapper >>> .ql-editor {
    min-height: 300px;
    max-height: 500px;
    overflow-y: auto;
}

.quill-editor-wrapper >>> .ql-container {
    font-size: 14px;
}

.quill-editor-wrapper >>> .ql-toolbar {
    border-top: 1px solid #d9d9d9;
    border-left: 1px solid #d9d9d9;
    border-right: 1px solid #d9d9d9;
    border-bottom: none;
    border-radius: 4px 4px 0 0;
}

.quill-editor-wrapper >>> .ql-container {
    border-bottom: 1px solid #d9d9d9;
    border-left: 1px solid #d9d9d9;
    border-right: 1px solid #d9d9d9;
    border-top: none;
    border-radius: 0 0 4px 4px;
}
</style>

